import { useCanViewEmails } from '../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import BreadIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory } from 'react-router-dom';
import { Resizable } from 're-resizable';
import { Tooltip } from '@mui/material';
import { cloneDeep } from 'lodash';
import { useOnClickOutside } from 'usehooks-ts';

import { getSubGroupsByGroupIdThunk, getUsersByOrganization } from '../../store/subGroups/subGroupsSlice';
import EditUserAdminModal from '../modules/Users/EditUserModal/EditUserModal';
import { GROUP, PATHS, SUBGROUP_LIST_ITEM, USER } from '../../enums';
import UpdateAnalystModal from '../SubGroupView (not used)/UpdateAnalystModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ContainedButton, OutlinedButton, RolesButton } from '../modules/Buttons';
import { ResizeIcon } from '../../assets/icons/resizeIcon';
import { ROLES, RolesLowerCase } from '../../enums/ROLES';
import EditIcon from '../../assets/icons/edit-pen.svg';
import FullPageLoader from '../modules/FullPageLoader';
import LinearProgress from '../modules/LinearProgress';
import DeleteAnalystModal from './DeleteAnalystModal';
import styles from './SubGroupManagement.module.scss';
import DeleteViewerModal from './DeleteViewerModal';
import { User } from '../modules/OrganizationCard';
import LinkAnalystModal from './LinkAnalystModal';
import { patchGroupAnalysts, patchSubGroupAnalysts } from '../../api';
import TrashButton from '../modules/TrashButton';
import SearchInput from '../modules/SearchInput';
import { DateFormatter } from '../../helpers';
import RolePlate from '../modules/RolePlate';
import { STATUS } from '../../enums/ENUMS';
import Divider from '../modules/Divider';
import Avatar from '../modules/Avatar';
import SubGroupsList from './SubGroupsList';
import { SortOptions } from '../../enums/SORT';
import { FE_VERSION } from '../../consts/version';
import { dateToFormattedHours } from '../../helpers/dateToFormattedHours';
import { MoreIcon } from '../../assets/icons/threedots';
import Modal from '../modules/Modal';
import { useDimensions } from '../../helpers';
import ApplyPreferencesModal from '../modules/ApplyPreferencesModal';
import { PermissionSelector } from '../modules/PermissionSelector';

enum ROLE_OPTIONS {
  ALL = 'All',
}

type ROLES_SELECTOR = RolesLowerCase | ROLE_OPTIONS;

type MenuContextType = 'sub_analyst' | 'sub_viewer' | 'group_analyst' | 'group_viewer';

const SubGroupManagementGeneralView = () => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { widthD } = useDimensions();
  const isMobile = widthD <= 1024;
  const [idToDelete, setIdToDelete] = useState('');
  const { currentGroup, status, sort, sortOption, subGroups } = useAppSelector((state) => state.subGroupsStore);
  const { role: currentRole, myOrganization } = useAppSelector((state) => state.user);

  const [currGroup, setCurrGroup] = useState<GROUP>();

  const parseUserRoles = (group: GROUP) => {
    try {
      const modifiedGroup = cloneDeep(group);
      modifiedGroup.organization_id = myOrganization?.id;
      const analysts: USER[] = [];
      const viewers: USER[] = [];
      modifiedGroup.analysts.forEach((user) => {
        if (analysts.find((u) => u.id === user.id)) return;

        const orgUser = user.organization_users?.find((u) => u?.organization?.id == modifiedGroup?.organization_id);
        if (orgUser && (orgUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN || orgUser?.role.title === RolesLowerCase.ANALYST)) {
          const updatedLastOnline = dateToFormattedHours(orgUser.last_online);

          analysts.push({ ...user, role: orgUser.role, last_online: updatedLastOnline, created_at: orgUser.created_at });
        }
      });

      modifiedGroup.viewers.forEach((user) => {
        if (viewers.find((u) => u.id === user.id)) return;

        const orgUser = user.organization_users?.find((u) => u?.organization?.id == modifiedGroup?.organization_id);
        if (orgUser && orgUser?.role?.title === RolesLowerCase.VIEWER) {
          const updatedLastOnline = dateToFormattedHours(orgUser.last_online);

          viewers.push({ ...user, role: orgUser.role, last_online: updatedLastOnline, created_at: orgUser.created_at });
        }
      });

      modifiedGroup.analysts = analysts;
      modifiedGroup.viewers = viewers;

      setCurrGroup(modifiedGroup);
    } catch (err) {
      console.log(err);
      setCurrGroup(group);
    }
  };

  useEffect(() => {
    if (currentGroup) {
      parseUserRoles(currentGroup);
    }
  }, [currentGroup]);

  const [showAnalystModal, setShowAnalystModal] = useState(false);
  const [showDeleteAnalystModal, setShowDeleteAnalystModal] = useState(false);
  const [showDeleteViewerModal, setShowDeleteViewerModal] = useState(false);

  const [fecthTrigger, setFetchTrigger] = useState(false);

  const groupId = history.location.pathname.match(/\/subgroups\/(\d+)/)?.[1];
  const sortOptionForRequest = sortOption === 'recent_views' ? SortOptions.NAME : sortOption;

  useEffect(() => {
    if (myOrganization?.id && groupId) {
      dispatch(getSubGroupsByGroupIdThunk({ groupId: groupId, sort, sortOption: sortOptionForRequest }));
      dispatch(getUsersByOrganization());
    }
  }, [myOrganization, sort, sortOptionForRequest, fecthTrigger, groupId, dispatch]);

  const fetchData = () => {
    setFetchTrigger(!fecthTrigger);
  };

  // modal handlers
  const switchAddAnalystModal = () => {
    setShowAnalystModal(!showAnalystModal);
  };

  const closeDeleteModal = () => {
    setShowDeleteAnalystModal(false);
    setShowDeleteViewerModal(false);
  };

  const toDeleteAnalyst = (id: string) => {
    setIdToDelete(id);
    setShowDeleteAnalystModal(true);
  };

  const toDeleteViewer = (id: string) => {
    setIdToDelete(id);
    setShowDeleteViewerModal(true);
  };

  const toGroups = () => {
    history.push(PATHS.GROUPS_MANAGEMENT.path);
  };

  const [userSearch, setUserSearch] = useState('');
  const onUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSearch(e.target.value);
  };

  const [selectedRole, setSelectedRole] = useState<ROLES_SELECTOR>(ROLE_OPTIONS.ALL);
  const roleFilterOptions = [
    { label: 'All', value: ROLE_OPTIONS.ALL },
    { label: 'OA', value: RolesLowerCase.ORGANIZATION_ADMIN },
    { label: 'Analyst', value: RolesLowerCase.ANALYST },
    { label: 'Viewer', value: RolesLowerCase.VIEWER },
  ];

  const [leftSideWidth, setLeftSideWidth] = useState(800);
  const leftSideRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLeftSideWidth(entry.contentRect.width);
      }
    });

    if (leftSideRef.current) {
      observer.observe(leftSideRef.current);
    }

    return () => {
      if (leftSideRef.current) {
        observer.unobserve(leftSideRef.current);
      }
    };
  }, []);

  const [selectedSubgroup, setSelectedSubgroup] = useState<SUBGROUP_LIST_ITEM | null>(null);
  const onSubgroupSelect = (sub: SUBGROUP_LIST_ITEM) => {
    if (showAnalystModal || showDeleteAnalystModal || showDeleteViewerModal) return;
    if (sub.id === selectedSubgroup?.id) {
      setSelectedSubgroup(null);
    } else {
      parseUserRolesForSubgroups(sub);
    }
  };

  const [loading, setLoading] = useState(false);
  const onSubgroupUserRemove = async (userId: string) => {
    if (loading) return;
    try {
      setLoading(true);
      await patchSubGroupAnalysts(selectedSubgroup!.id, {
        usersToRemove: [userId],
        groupId: currGroup!.id,
      });
      if (groupId) {
        await dispatch(getSubGroupsByGroupIdThunk({ groupId, sort, sortOption: sortOptionForRequest }));
      }
      await dispatch(getUsersByOrganization());
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const [showChangeDownloadPreferenceModal, setShowChangeDownloadPreferenceModal] = useState(false);
  const [showConfirmDownloadPreferenceModal, setShowConfirmDownloadPreferenceModal] = useState(false);
  const [pendingDownloadPreference, setPendingDownloadPreference] = useState<boolean | null>(null);
  const [showGroupPreferencesModal, setShowGroupPreferencesModal] = useState(false);
  const [showUserPreferencesModal, setShowUserPreferencesModal] = useState(false);
  const [selectedUserForPreferences, setSelectedUserForPreferences] = useState<USER | null>(null);

  const switchChangeDownloadPreferenceModal = () => {
    if (loading) return;
    setShowChangeDownloadPreferenceModal((prev) => !prev);
  };

  const closeConfirmDownloadPreferenceModal = () => {
    if (loading) return;
    setShowConfirmDownloadPreferenceModal(false);
    setPendingDownloadPreference(null);
  };

  const chooseDownloadPreference = (allowDownload: boolean) => {
    setPendingDownloadPreference(allowDownload);
    setShowChangeDownloadPreferenceModal(false);
    setShowConfirmDownloadPreferenceModal(true);
  };

  const applyDownloadPreference = async () => {
    if (loading || pendingDownloadPreference === null || !currGroup?.id) return;

    try {
      setLoading(true);

      if (selectedSubgroup) {
        await patchSubGroupAnalysts(selectedSubgroup.id, {
          groupId: currGroup.id,
          allow_download_own_composers: pendingDownloadPreference,
        });
      } else {
        await patchGroupAnalysts(currGroup.id, {
          allow_download_own_composers: pendingDownloadPreference,
        });
      }

      if (groupId) {
        await dispatch(getSubGroupsByGroupIdThunk({ groupId, sort, sortOption: sortOptionForRequest }));
      }
      await dispatch(getUsersByOrganization());

      notification.showMessage(`Download preferences were updated for users in this ${selectedSubgroup ? 'subgroup' : 'group'}`);
      setShowConfirmDownloadPreferenceModal(false);
      setPendingDownloadPreference(null);
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSubgroup && subGroups) {
      const sub = subGroups.find((subG) => subG.id === selectedSubgroup!.id);
      if (sub) {
        parseUserRolesForSubgroups(sub);
      } else {
        setSelectedSubgroup(null);
      }
    }
  }, [subGroups, myOrganization, selectedSubgroup]);

  const parseUserRolesForSubgroups = (group: GROUP | SUBGROUP_LIST_ITEM) => {
    try {
      const modifiedGroup = cloneDeep(group);
      modifiedGroup.organization_id = myOrganization?.id;
      const analysts: USER[] = [];
      const viewers: USER[] = [];
      modifiedGroup.analysts.forEach((user) => {
        if (analysts.find((u) => u.id === user.id)) return;
        const updatedLastOnline = dateToFormattedHours(user.last_online);

        analysts.push({ ...user, last_online: updatedLastOnline });
      });

      modifiedGroup.viewers.forEach((user) => {
        if (viewers.find((u) => u.id === user.id)) return;
        const updatedLastOnline = dateToFormattedHours(user.last_online);

        viewers.push({ ...user, last_online: updatedLastOnline });
      });

      modifiedGroup.analysts = analysts;
      modifiedGroup.viewers = viewers;

      setSelectedSubgroup(modifiedGroup);
    } catch (err) {
      console.log(err);
      setSelectedSubgroup(group as SUBGROUP_LIST_ITEM);
    }
  };

  const [selectedUserToEdit, setSelectedUserToEdit] = useState<USER | User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const switchEditModal = (u?: any) => {
    if (showEditModal) {
      setSelectedUserToEdit(null);
    }
    if (u) {
      setSelectedUserToEdit(u);
    }
    setShowEditModal(!showEditModal);
  };

  // ---- Three dots menu for roleAction ----
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [menuUser, setMenuUser] = useState<USER | null>(null);
  const [menuContext, setMenuContext] = useState<MenuContextType | null>(null);

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const openRoleActionMenu = (e: React.MouseEvent<HTMLDivElement>, u: USER, ctx: MenuContextType) => {
    e.preventDefault();
    e.stopPropagation();

    const rowElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!rowElement) return;

    const rowRect = rowElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 220;
    const menuHeight = actionMenuRef.current?.offsetHeight || 160;

    let left = e.clientX;
    let top = e.clientY;

    // clamp horizontally inside row boundaries
    const maxLeft = rowRect.right - menuWidth;
    if (left > maxLeft) {
      left = Math.max(maxLeft, rowRect.left);
    }
    if (left < rowRect.left) {
      left = rowRect.left;
    }

    // vertical clamp vs viewport bottom
    const viewportBottom = window.innerHeight;
    if (top + menuHeight > viewportBottom) {
      const diff = top + menuHeight - viewportBottom;
      if (diff > 90) {
        top = Math.max(top - (menuHeight - 100), 0);
      } else {
        top = Math.max(top - diff, 0);
      }
    }

    setMenuPosition({ x: left, y: top });
    setMenuUser(u);
    setMenuContext(ctx);
    setShowDotsMenu(true);
  };

  const handleMenuEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!menuUser) return;
    switchEditModal(menuUser);
    setShowDotsMenu(false);
  };

  const handleMenuDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!menuUser || !menuContext) return;

    if (menuContext === 'sub_analyst' || menuContext === 'sub_viewer') {
      onSubgroupUserRemove(menuUser.id);
    } else if (menuContext === 'group_analyst') {
      toDeleteAnalyst(menuUser.id);
    } else if (menuContext === 'group_viewer') {
      toDeleteViewer(menuUser.id);
    }

    setShowDotsMenu(false);
  };

  const handleMenuPreferences = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!menuUser) return;
    setSelectedUserForPreferences(menuUser);
    setShowUserPreferencesModal(true);
    setShowDotsMenu(false);
  };
  // ----------------------------------------

  return (
    <div className={styles.container}>
      <div className={styles.breadCrumb} onClick={toGroups}>
        Groups Management <BreadIcon style={{ fontSize: '16px', margin: '0 6px' }} />
        <span>{myOrganization?.name} </span>
        <BreadIcon style={{ fontSize: '16px', margin: '0 6px' }} /> <span>{currGroup?.title} </span>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.pageTitle}>{currGroup?.title}</div>
      <div className={styles.mainView}>
        {isMobile ? (
          <div className={styles.leftSide} ref={leftSideRef}>
            <SubGroupsList
              width={isMobile ? widthD : leftSideWidth}
              sort={sort}
              sortOption={sortOption}
              managementList={currGroup?.analysts}
              viewersList={currGroup?.viewers}
              onSubgroupSelect={onSubgroupSelect}
              selectedSubgroup={selectedSubgroup}
            />
            <div className={styles.version}>Amplify version: {FE_VERSION}</div>
          </div>
        ) : (
          <Resizable
            enable={{
              right: true,
              top: false,
              bottom: false,
              left: false,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false,
            }}
            maxWidth={'50%'}
            minWidth={'30%'}
            defaultSize={{
              width: '800px',
              height: '100%',
            }}
            handleComponent={{
              right: (
                <>
                  <div style={{ width: '3px', height: '100%', background: '#183047', borderRadius: '12px', position: 'relative' }}>
                    <div style={{ position: 'absolute', borderRadius: '12px', left: '-21px', top: '53px' }}>
                      <ResizeIcon />
                    </div>
                  </div>
                </>
              ),
            }}
          >
            <div className={styles.leftSide} ref={leftSideRef}>
              <SubGroupsList
                width={isMobile ? widthD : leftSideWidth}
                sort={sort}
                sortOption={sortOption}
                managementList={currGroup?.analysts}
                viewersList={currGroup?.viewers}
                onSubgroupSelect={onSubgroupSelect}
                selectedSubgroup={selectedSubgroup}
              />
              <div className={styles.version}>Amplify version: {FE_VERSION}</div>
            </div>
          </Resizable>
        )}

        <div className={styles.rightSide}>
          <div className={styles.analystList}>
            <div className={styles.listHeader}>
              <div className={styles.heading}>Participants {selectedSubgroup ? `of ${selectedSubgroup.title}` : ''}</div>
              {currentRole === ROLES.ORGANIZATION_ADMIN && (
                <div className={styles.headerActions}>
                  <ContainedButton onClick={switchChangeDownloadPreferenceModal} className={styles.btn}>
                    <span className={styles.desktopActionLabel}>Change Download Preferences</span>
                    <span className={styles.compactActionLabel}>Download Prefs</span>
                  </ContainedButton>
                  <ContainedButton onClick={() => setShowGroupPreferencesModal(true)} className={styles.btn}>
                    <span className={styles.desktopActionLabel}>Group Preferences</span>
                    <span className={styles.compactActionLabel}>Prefs</span>
                  </ContainedButton>
                  <ContainedButton onClick={switchAddAnalystModal} className={styles.btn}>
                    <span className={styles.desktopActionLabel}>+ Add Members</span>
                    <span className={styles.compactActionLabel}>Add Members</span>
                  </ContainedButton>
                </div>
              )}
            </div>

            <div className={styles.searchWrapper}>
              <SearchInput className={styles.search} onChange={onUserSearch} placeHolder="Search user" />
              <div className={styles.toolbarRoles}>
                <RolesButton
                  kind={selectedRole === ROLE_OPTIONS.ALL ? 'white' : 'gray'}
                  hover={selectedRole !== ROLE_OPTIONS.ALL ? 'white' : 'none'}
                  onClick={() => setSelectedRole(ROLE_OPTIONS.ALL)}
                  customStyles={{ borderRadius: '8px' }}
                >
                  <div className={styles.roleButton}>All {selectedRole === ROLE_OPTIONS.ALL && <CheckIcon sx={{ height: '12px' }} />}</div>
                </RolesButton>
                <RolesButton
                  kind={selectedRole === RolesLowerCase.ORGANIZATION_ADMIN ? 'orange' : 'gray'}
                  hover={selectedRole !== RolesLowerCase.ORGANIZATION_ADMIN ? 'orange' : 'none'}
                  onClick={() => setSelectedRole(RolesLowerCase.ORGANIZATION_ADMIN)}
                  customStyles={{ borderRadius: '8px', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>
                    OA {selectedRole === RolesLowerCase.ORGANIZATION_ADMIN && <CheckIcon sx={{ height: '12px' }} />}
                  </div>
                </RolesButton>
                <RolesButton
                  kind={selectedRole === RolesLowerCase.ANALYST ? 'blue' : 'gray'}
                  hover={selectedRole !== RolesLowerCase.ANALYST ? 'blue' : 'none'}
                  onClick={() => setSelectedRole(RolesLowerCase.ANALYST)}
                  customStyles={{ borderRadius: '8px', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>
                    Analyst {selectedRole === RolesLowerCase.ANALYST && <CheckIcon sx={{ height: '12px' }} />}
                  </div>
                </RolesButton>
                <RolesButton
                  kind={selectedRole === RolesLowerCase.VIEWER ? 'green' : 'gray'}
                  hover={selectedRole !== RolesLowerCase.VIEWER ? 'green' : 'none'}
                  onClick={() => setSelectedRole(RolesLowerCase.VIEWER)}
                  customStyles={{ borderRadius: '8px', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>
                    Viewer {selectedRole === RolesLowerCase.VIEWER && <CheckIcon sx={{ height: '12px' }} />}
                  </div>
                </RolesButton>
              </div>
              <PermissionSelector
                title="Role"
                selectedValues={[
                  roleFilterOptions.find((option) => option.value === selectedRole) || roleFilterOptions[0],
                ]}
                options={roleFilterOptions}
                className={styles.roleDropdown}
                onSelect={(values) => setSelectedRole(values[0].value as ROLES_SELECTOR)}
                singleValueSelector
                withoutCheckBox
                style={{ top: '36px', bottom: 'auto', right: '0', width: '150px' }}
              />
            </div>

            <div className={styles.userListHeader}>
              <div className={styles.title}>Name{canViewEmails && '/email'}</div>
              <div className={styles.role}>Role</div>
              <div className={styles.created}>Created Date</div>
              <div className={styles.login}>Last Active</div>
              <div className={styles.action}>Action</div>
            </div>
            {loading && <LinearProgress />}

            {selectedSubgroup ? (
              <div className={styles.userList}>
                {selectedSubgroup?.analysts
                  .filter((u) => u?.email.includes(userSearch) || u?.username.includes(userSearch))
                  .filter((u) => selectedRole === ROLE_OPTIONS.ALL || selectedRole === u.role.title)
                  .map((analyst) => (
                    <div className={styles.li} key={analyst.id}>
                      <div
                        className={styles.container}
                        onContextMenu={
                          currentRole === ROLES.ORGANIZATION_ADMIN
                            ? (e) => openRoleActionMenu(e, analyst as USER, 'sub_analyst')
                            : undefined
                        }
                      >
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={analyst?.avatar_url} name={analyst.email[0]} />
                            <div className={styles.name}>
                              {analyst?.username} {canViewEmails && (<div className={styles.email}>{analyst?.email}</div>)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!analyst.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!analyst.invitation_token ? styles.active : styles.notActive}>
                              <RolePlate userRole={analyst.role.title} />
                            </div>
                          </Tooltip>
                        </div>
                        <div className={styles.created}>{DateFormatter(analyst.created_at, true)}</div>

                        <div className={styles.login}>{analyst.last_online}</div>
                        <div className={styles.roleAction}>
                          {currentRole === ROLES.ORGANIZATION_ADMIN && (
                            <div className={styles.dotsMenuBtn} onClick={(e) => openRoleActionMenu(e, analyst as USER, 'sub_analyst')}>
                              <div className={styles.dotIcon}>
                                <MoreIcon />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                {selectedSubgroup?.viewers
                  .filter((u) => u?.email.includes(userSearch) || u?.username.includes(userSearch))
                  .filter((u) => selectedRole === ROLE_OPTIONS.ALL || selectedRole === u.role.title)
                  .map((viewer) => (
                    <div className={styles.li} key={viewer.id}>
                      <div
                        className={styles.container}
                        onContextMenu={
                          currentRole !== ROLES.VIEWER && currentRole !== ROLES.ANALYST
                            ? (e) => openRoleActionMenu(e, viewer as USER, 'sub_viewer')
                            : undefined
                        }
                      >
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={viewer?.avatar_url} name={viewer.email[0]} />
                            <div className={styles.name}>
                              {viewer?.username} {canViewEmails && (<div className={styles.email}>{viewer?.email}</div>)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!viewer.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!viewer.invitation_token ? styles.active : styles.notActive}>
                              <RolePlate userRole={viewer.role.title} />
                            </div>
                          </Tooltip>
                        </div>

                        <div className={styles.created}>{DateFormatter(viewer.created_at, true)}</div>

                        <div className={styles.login}>{viewer.last_online}</div>
                        <div className={styles.roleAction}>
                          {currentRole !== ROLES.VIEWER && currentRole !== ROLES.ANALYST && (
                            <div className={styles.dotsMenuBtn} onClick={(e) => openRoleActionMenu(e, viewer as USER, 'sub_viewer')}>
                              <div className={styles.dotIcon}>
                                <MoreIcon />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.userList}>
                {currGroup?.analysts
                  .filter((u) => u?.email.includes(userSearch) || u?.username.includes(userSearch))
                  .filter((u) => selectedRole === ROLE_OPTIONS.ALL || selectedRole === u.role.title)
                  .map((analyst) => (
                    <div className={styles.li} key={analyst.id}>
                      <div
                        className={styles.container}
                        onContextMenu={
                          currentRole === ROLES.ORGANIZATION_ADMIN
                            ? (e) => openRoleActionMenu(e, analyst as USER, 'group_analyst')
                            : undefined
                        }
                      >
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={analyst?.avatar_url} name={analyst.email[0]} />
                            <div className={styles.name}>
                              {analyst?.username} {canViewEmails && (<div className={styles.email}>{analyst?.email}</div>)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!analyst.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!analyst.invitation_token ? styles.active : styles.notActive}>
                              <RolePlate
                                userRole={
                                  analyst.organization_users?.length
                                    ? analyst.organization_users.find((user) => user.organization.id == currGroup.organization_id)!.role
                                        .title
                                    : `${analyst.role}`
                                }
                              />
                            </div>
                          </Tooltip>
                        </div>
                        <div className={styles.created}>{DateFormatter(analyst.created_at, true)}</div>

                        <div className={styles.login}>{analyst.last_online}</div>
                        <div className={styles.roleAction}>
                          {currentRole === ROLES.ORGANIZATION_ADMIN && (
                            <div className={styles.dotsMenuBtn} onClick={(e) => openRoleActionMenu(e, analyst as USER, 'group_analyst')}>
                              <div className={styles.dotIcon}>
                                <MoreIcon />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                {currGroup?.viewers
                  .filter((u) => u?.email.includes(userSearch) || u?.username.includes(userSearch))
                  .filter((u) => selectedRole === ROLE_OPTIONS.ALL || selectedRole === u.role.title)
                  .map((viewer) => (
                    <div className={styles.li} key={viewer.id}>
                      <div
                        className={styles.container}
                        onContextMenu={
                          currentRole !== ROLES.VIEWER && currentRole !== ROLES.ANALYST
                            ? (e) => openRoleActionMenu(e, viewer as USER, 'group_viewer')
                            : undefined
                        }
                      >
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={viewer?.avatar_url} name={viewer.email[0]} />
                            <div className={styles.name}>
                              {viewer?.username} {canViewEmails && (<div className={styles.email}>{viewer?.email}</div>)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!viewer.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!viewer.invitation_token ? styles.active : styles.notActive}>
                              <RolePlate
                                userRole={
                                  viewer.organization_users?.length
                                    ? viewer.organization_users.find((user) => user.organization.id == currGroup.organization_id)!.role
                                        .title
                                    : `${viewer.role}`
                                }
                              />
                            </div>
                          </Tooltip>
                        </div>

                        <div className={styles.created}>{DateFormatter(viewer.created_at, true)}</div>

                        <div className={styles.login}>{viewer.last_online}</div>
                        <div className={styles.roleAction}>
                          {currentRole !== ROLES.VIEWER && currentRole !== ROLES.ANALYST && (
                            <div className={styles.dotsMenuBtn} onClick={(e) => openRoleActionMenu(e, viewer as USER, 'group_viewer')}>
                              <div className={styles.dotIcon}>
                                <MoreIcon />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shared 3-dots action menu for all rows */}
      <div
        ref={actionMenuRef}
        className={styles.actionMenu}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          display: showDotsMenu ? 'block' : 'none',
          top: `${menuPosition.y}px`,
          left: `${menuPosition.x}px`,
          zIndex: 9999,
        }}
      >
        <div className={styles.menuOption} onClick={handleMenuEdit}>
          <img src={EditIcon} alt="edit" />
          <span>Edit user</span>
        </div>
        <div className={styles.menuOption} onClick={handleMenuPreferences}>
          <img src={EditIcon} alt="preferences" />
          <span>User preferences</span>
        </div>
        <div className={styles.menuOption} onClick={handleMenuDelete}>
          <TrashButton />
          <span>
            {menuContext === 'sub_analyst' || menuContext === 'sub_viewer'
              ? 'Remove from subgroup'
              : menuContext === 'group_analyst'
                ? 'Delete user'
                : 'Delete user'}
          </span>
        </div>
      </div>

      <Modal open={showChangeDownloadPreferenceModal} onClose={switchChangeDownloadPreferenceModal}>
        <div className={styles.downloadPreferenceModalBody}>
          <div className={styles.modalEyebrow}>Download Composer</div>
          <div className={styles.modalTitle}>Force Download Composer access for this {selectedSubgroup ? 'subgroup' : 'group'}</div>
          <div className={styles.modalDescription}>
            Apply this change to all users in this {selectedSubgroup ? 'subgroup' : 'group'}.
          </div>
          <div className={styles.actionBtnsFlex}>
            <div className={`${styles.preferenceChoiceBtn} ${styles.forceOnBtn}`} onClick={() => chooseDownloadPreference(true)}>
              Force On
            </div>
            <div className={`${styles.preferenceChoiceBtn} ${styles.forceOffBtn}`} onClick={() => chooseDownloadPreference(false)}>
              Force Off
            </div>
          </div>
          <div className={styles.actionBtns}>
            <OutlinedButton kind="default" onClick={switchChangeDownloadPreferenceModal}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      </Modal>

      <Modal open={showConfirmDownloadPreferenceModal} onClose={closeConfirmDownloadPreferenceModal} loading={loading}>
        <div className={styles.downloadPreferenceModalBody}>
          <div className={`${styles.modalTitle} ${styles.confirmTitle}`}>
            Are you sure you want to force {pendingDownloadPreference ? 'ON' : 'OFF'} download own online composers for all users in this{' '}
            {selectedSubgroup ? 'subgroup' : 'group'}?
          </div>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={applyDownloadPreference} kind="default" disabled={loading}>
              Yes, I am sure
            </OutlinedButton>
            <ContainedButton kind="purple" onClick={closeConfirmDownloadPreferenceModal} disabled={loading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </Modal>

      <FullPageLoader loading={status === STATUS.LOADING} />
      {!selectedSubgroup && <LinkAnalystModal switchModal={switchAddAnalystModal} open={showAnalystModal} />}
      <DeleteAnalystModal id={idToDelete} switchModal={closeDeleteModal} open={showDeleteAnalystModal} />
      <DeleteViewerModal id={idToDelete} switchModal={closeDeleteModal} open={showDeleteViewerModal} />
      {selectedSubgroup && <UpdateAnalystModal switchModal={switchAddAnalystModal} open={showAnalystModal} subGroup={selectedSubgroup} />}
      {selectedUserToEdit && (
        // @ts-ignore
        <EditUserAdminModal fetchData={fetchData} switchModal={switchEditModal} isOpen={showEditModal} user={selectedUserToEdit} />
      )}
      <ApplyPreferencesModal
        open={showGroupPreferencesModal}
        onClose={() => setShowGroupPreferencesModal(false)}
        title={`${selectedSubgroup ? `${selectedSubgroup.title} (subgroup)` : currGroup?.title || 'Group'} Preferences`}
        fixedScope="group"
        fixedGroupId={Number(selectedSubgroup ? selectedSubgroup.id : currGroup?.id)}
      />
      <ApplyPreferencesModal
        open={showUserPreferencesModal}
        onClose={() => {
          setShowUserPreferencesModal(false);
          setSelectedUserForPreferences(null);
        }}
        title={selectedUserForPreferences ? `${selectedUserForPreferences.username || (canViewEmails ? selectedUserForPreferences.email : 'User')} Preferences` : 'User Preferences'}
        fixedScope="user"
        fixedUserId={selectedUserForPreferences ? Number(selectedUserForPreferences.id) : undefined}
      />
    </div>
  );
};

export default SubGroupManagementGeneralView;
